import { createJurisdictionHeaders } from '@/data';
import { URLS } from '@/data/config';
import { useInfiniteQuery } from '@tanstack/react-query';

export interface CasinoTransaction {
    bet_id: string;
    external_id: string;
    round_id: string;
    date: string;
    amount: number;
    status: string;
    winning_amount: number;
    game_name: string;
}

export interface CasinoHistoryPagination {
    page: number;
    limit: number;
    has_more: boolean;
}

export interface CasinoHistoryResponse {
    data: {
        transactions: CasinoTransaction[];
        pagination: CasinoHistoryPagination;
    };
}

export const getCasinoHistory = async (pageParam: number): Promise<CasinoHistoryResponse> => {
    const params = new URLSearchParams({
        page: String(pageParam),
        with_total: '0', //Fast query
    }).toString();

    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/casino/transaction-history?${params}`, {
        method: 'GET',
        headers: {
            ...createJurisdictionHeaders(),
            JURISDICTION: 'CASH-CASINO',
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();

    if (!response.ok) {
        throw data;
    }
    return data;
};

export function useCasinoHistory() {
    return useInfiniteQuery({
        queryKey: ['casino-transaction-history'],
        initialPageParam: 1,
        queryFn: ({ pageParam = 1 }) => getCasinoHistory(pageParam),
        getNextPageParam: res => {
            const { page, has_more } = res.data.pagination;
            return has_more ? page + 1 : undefined;
        },
        select: data => {
            const validTransactions = data?.pages.map(page => page.data).filter(Boolean);
            return {
                pageParams: data.pageParams,
                pages: validTransactions?.flatMap(page => page.transactions) || [],
            };
        },
        staleTime: 0,
        gcTime: 0,
    });
}
